/**
 * FundingHistoryTable Component
 * Renders a table of funding instructions with date, amount, window, and status badges.
 */

import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';
import type { FundingInstruction } from '@/types/funding.types';

// ─── Helpers ─────────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'PENDING_FUNDING':
      return 'warning';
    case 'INCLUDED':
      return 'info';
    case 'CONFIRMED':
      return 'success';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING_FUNDING':
      return 'Pendiente';
    case 'INCLUDED':
      return 'Incluido';
    case 'CONFIRMED':
      return 'Confirmado';
    default:
      return status;
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface FundingHistoryTableProps {
  items: FundingInstruction[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export function FundingHistoryTable({ items }: FundingHistoryTableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/50 text-xs uppercase text-muted-foreground">
            <th className="px-4 py-3">ID Fondeo</th>
            <th className="px-4 py-3">ID Credito</th>
            <th className="px-4 py-3">Ventana</th>
            <th className="px-4 py-3 text-right">Monto</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Fecha</th>
          </tr>
        </thead>
        <tbody>
          {(items ?? []).map((item) => (
            <tr
              key={item.funding_id}
              className="border-b border-border last:border-0 hover:bg-muted/50"
            >
              <td className="px-4 py-3 font-mono text-xs">{item.funding_id}</td>
              <td className="px-4 py-3 font-mono text-xs">{item.credit_id}</td>
              <td className="px-4 py-3">{item.funding_window}</td>
              <td className="px-4 py-3 text-right tabular-nums">
                {formatCurrency(item.amount_mxn, 'MXN', 'es-MX')}
              </td>
              <td className="px-4 py-3">
                <Badge variant={getStatusVariant(item.status)}>
                  {getStatusLabel(item.status)}
                </Badge>
              </td>
              <td className="px-4 py-3">{formatDateShort(item.created_at)}</td>
            </tr>
          ))}
          {(items ?? []).length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                No hay registros de historial
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
